import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageCard } from '../../components/layout/page/PageCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PasswordInput } from '../../components/ui/PasswordInput';
import {
  fetchSystemSettings,
  saveSystemSettings,
  TenantSystemSettings,
} from '../../services/systemSettingsApi';
import { SystemSettingsTabId } from './SystemSettingsTabId';

const SETTINGS_TABS: SettingsTabItem<SystemSettingsTabId>[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'smtp', label: 'SMTP' },
  { id: 'imap', label: 'IMAP' },
  { id: 'security', label: 'Segurança e autenticação' },
  { id: 'notifications', label: 'Notificações' },
  { id: 'notes', label: 'Anotações' },
  { id: 'taskQueue', label: 'Fila de Tarefas' },
];

export function SystemSettingsPage() {
  const [settings, setSettings] = useState<TenantSystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<SystemSettingsTabId>('dashboard');

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        setSettings(await fetchSystemSettings());
      } catch {
        toast.error('Falha ao carregar configurações.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!settings) return;

    if (!settings.authentication.allowEmailPassword && !settings.authentication.allowGoogle) {
      toast.error('Selecione pelo menos um método de autenticação.');
      return;
    }

    if (
      settings.authentication.allowGoogle
      && !settings.authentication.googleAllowedDomain.trim()
    ) {
      toast.error('Informe o domínio corporativo Google (ex.: empresa.com.br).');
      return;
    }

    if (
      settings.authentication.allowGoogle
      && !settings.authentication.googleClientId.trim()
    ) {
      toast.error('Informe o Client ID OAuth do Google.');
      return;
    }

    setSaving(true);
    try {
      const saved = await saveSystemSettings(settings);
      setSettings(saved);
      toast.success('Configurações salvas.');
    } catch {
      toast.error('Falha ao salvar configurações.');
    } finally {
      setSaving(false);
    }
  }

  if (loading || !settings) {
    return <LoadingSpinner label="Carregando configurações..." />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Configurações do sistema"
        description="Parâmetros da organização armazenados em JSON — ajuste por área e salve quando terminar."
      />

      <form onSubmit={(event) => void handleSubmit(event)} className="erp-form">
        <SettingsTabBar tabs={SETTINGS_TABS} activeTab={activeTab} onChange={setActiveTab} />

        {activeTab === 'dashboard' ? (
          <PageCard
            role="tabpanel"
            aria-label="Dashboard"
            title="Dashboard"
            subtitle="Tempo de cache dos painéis executivo e de prospecção."
          >
            <label className="erp-label">
              Tempo de cache (segundos)
              <input
                type="number"
                min={30}
                max={3600}
                className="erp-input-inline"
                value={settings.dashboard.cacheSeconds}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    dashboard: { cacheSeconds: Number(event.target.value) },
                  })
                }
              />
            </label>
            <p className="erp-hint">Mínimo 30s, máximo 3600s (1 hora).</p>
          </PageCard>
        ) : null}

        {activeTab === 'smtp' ? (
          <PageCard
            role="tabpanel"
            aria-label="SMTP"
            title="SMTP (envio)"
            subtitle="Servidor de e-mail para envios automáticos do sistema."
          >
            <div className="erp-form-grid">
              <label className="erp-label erp-form-grid-full">
                Host
                <input
                  className="erp-input"
                  value={settings.smtp.host}
                  onChange={(event) =>
                    setSettings({ ...settings, smtp: { ...settings.smtp, host: event.target.value } })
                  }
                />
              </label>
              <label className="erp-label">
                Porta
                <input
                  type="number"
                  className="erp-input"
                  value={settings.smtp.port}
                  onChange={(event) =>
                    setSettings({ ...settings, smtp: { ...settings.smtp, port: Number(event.target.value) } })
                  }
                />
              </label>
              <label className="erp-label-inline sm:pt-8">
                <input
                  type="checkbox"
                  checked={settings.smtp.useTls}
                  onChange={(event) =>
                    setSettings({ ...settings, smtp: { ...settings.smtp, useTls: event.target.checked } })
                  }
                />
                Usar TLS
              </label>
              <label className="erp-label">
                Usuário
                <input
                  className="erp-input"
                  value={settings.smtp.username}
                  onChange={(event) =>
                    setSettings({ ...settings, smtp: { ...settings.smtp, username: event.target.value } })
                  }
                />
              </label>
              <label className="erp-label">
                Senha
                <PasswordInput
                  className="erp-input pr-11 !mt-0"
                  wrapperClassName="mt-1 w-full"
                  value={settings.smtp.password}
                  onChange={(event) =>
                    setSettings({ ...settings, smtp: { ...settings.smtp, password: event.target.value } })
                  }
                />
              </label>
              <label className="erp-label">
                Remetente (e-mail)
                <input
                  className="erp-input"
                  value={settings.smtp.fromAddress}
                  onChange={(event) =>
                    setSettings({ ...settings, smtp: { ...settings.smtp, fromAddress: event.target.value } })
                  }
                />
              </label>
              <label className="erp-label">
                Nome do remetente
                <input
                  className="erp-input"
                  value={settings.smtp.fromName}
                  onChange={(event) =>
                    setSettings({ ...settings, smtp: { ...settings.smtp, fromName: event.target.value } })
                  }
                />
              </label>
            </div>
          </PageCard>
        ) : null}

        {activeTab === 'imap' ? (
          <PageCard
            role="tabpanel"
            aria-label="IMAP"
            title="IMAP (recebimento)"
            subtitle="Caixa de entrada para leitura de respostas e notificações."
          >
            <div className="erp-form-grid">
              <label className="erp-label erp-form-grid-full">
                Host
                <input
                  className="erp-input"
                  value={settings.imap.host}
                  onChange={(event) =>
                    setSettings({ ...settings, imap: { ...settings.imap, host: event.target.value } })
                  }
                />
              </label>
              <label className="erp-label">
                Porta
                <input
                  type="number"
                  className="erp-input"
                  value={settings.imap.port}
                  onChange={(event) =>
                    setSettings({ ...settings, imap: { ...settings.imap, port: Number(event.target.value) } })
                  }
                />
              </label>
              <label className="erp-label-inline sm:pt-8">
                <input
                  type="checkbox"
                  checked={settings.imap.useSsl}
                  onChange={(event) =>
                    setSettings({ ...settings, imap: { ...settings.imap, useSsl: event.target.checked } })
                  }
                />
                Usar SSL
              </label>
              <label className="erp-label">
                Usuário
                <input
                  className="erp-input"
                  value={settings.imap.username}
                  onChange={(event) =>
                    setSettings({ ...settings, imap: { ...settings.imap, username: event.target.value } })
                  }
                />
              </label>
              <label className="erp-label">
                Senha
                <PasswordInput
                  className="erp-input pr-11 !mt-0"
                  wrapperClassName="mt-1 w-full"
                  value={settings.imap.password}
                  onChange={(event) =>
                    setSettings({ ...settings, imap: { ...settings.imap, password: event.target.value } })
                  }
                />
              </label>
            </div>
          </PageCard>
        ) : null}

        {activeTab === 'security' ? (
          <PageCard
            role="tabpanel"
            aria-label="Segurança e autenticação"
            title="Segurança e autenticação"
            subtitle="Defina quais métodos de login ficam disponíveis para os colaboradores da empresa."
          >
            <div className="space-y-4">
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={settings.authentication.allowEmailPassword}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      authentication: {
                        ...settings.authentication,
                        allowEmailPassword: event.target.checked,
                      },
                    })
                  }
                />
                E-mail e senha
              </label>
              <p className="erp-hint">
                Login tradicional com credenciais cadastradas no ERP e recuperação de senha por e-mail.
              </p>

              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={settings.authentication.allowGoogle}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      authentication: {
                        ...settings.authentication,
                        allowGoogle: event.target.checked,
                      },
                    })
                  }
                />
                Google (Workspace)
              </label>
              <p className="erp-hint">
                Login social com contas Google Workspace do domínio corporativo abaixo.
              </p>

              {settings.authentication.allowGoogle ? (
                <>
                  <label className="erp-label">
                    Client ID OAuth (Google Cloud)
                    <input
                      className="erp-input"
                      placeholder="123456789.apps.googleusercontent.com"
                      value={settings.authentication.googleClientId}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          authentication: {
                            ...settings.authentication,
                            googleClientId: event.target.value.trim(),
                          },
                        })
                      }
                    />
                  </label>
                  <p className="erp-hint">
                    Credencial OAuth 2.0 (tipo Aplicativo Web). Origem autorizada: URL do ERP
                    (ex. https://go.baruk.ai).
                  </p>

                  <label className="erp-label">
                    Client ID OAuth — extensão Chrome
                    <input
                      className="erp-input"
                      placeholder="123456789.apps.googleusercontent.com"
                      value={settings.authentication.googleExtensionClientId}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          authentication: {
                            ...settings.authentication,
                            googleExtensionClientId: event.target.value.trim(),
                          },
                        })
                      }
                    />
                  </label>
                  <p className="erp-hint">
                    Credencial OAuth para a extensão Baruk Go Collector. Tipo recomendado:{' '}
                    <strong>Aplicativo Web</strong> (não o Client ID do site). URI de redirecionamento
                    autorizada: https://{'<ID da extensão>'}.chromiumapp.org/ — confira o ID em
                    chrome://extensions após recarregar a extensão.
                  </p>

                  <label className="erp-label">
                    Domínio corporativo Google
                  <input
                    className="erp-input"
                    placeholder="ex.: baruk.ai"
                    value={settings.authentication.googleAllowedDomain}
                    onChange={(event) =>
                      setSettings({
                        ...settings,
                        authentication: {
                          ...settings.authentication,
                          googleAllowedDomain: event.target.value
                            .trim()
                            .replace(/^@+/, '')
                            .toLowerCase(),
                        },
                      })
                    }
                  />
                </label>
                <p className="erp-hint">
                  Somente contas @{settings.authentication.googleAllowedDomain || 'seu-dominio'} do Google Workspace
                  poderão entrar. Contas pessoais @gmail.com serão recusadas.
                </p>
                </>
              ) : null}

              {!settings.authentication.allowEmailPassword && !settings.authentication.allowGoogle ? (
                <p className="text-sm text-rose-600">
                  Pelo menos um método deve permanecer ativo para que os usuários consigam entrar.
                </p>
              ) : null}
            </div>
          </PageCard>
        ) : null}

        {activeTab === 'notifications' ? (
          <PageCard
            role="tabpanel"
            aria-label="Notificações"
            title="Notificações de tarefas"
            subtitle="Política da organização para envio de alertas do planejamento estratégico."
          >
            <div className="space-y-3">
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={settings.taskNotifications.enabled}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      taskNotifications: {
                        ...settings.taskNotifications,
                        enabled: event.target.checked,
                      },
                    })
                  }
                />
                Habilitar notificações de tarefas
              </label>
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={settings.taskNotifications.notifyTaskOwner}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      taskNotifications: {
                        ...settings.taskNotifications,
                        notifyTaskOwner: event.target.checked,
                      },
                    })
                  }
                />
                Notificar responsável (owner)
              </label>
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={settings.taskNotifications.notifyTaskExecutor}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      taskNotifications: {
                        ...settings.taskNotifications,
                        notifyTaskExecutor: event.target.checked,
                      },
                    })
                  }
                />
                Notificar executor
              </label>
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={settings.taskNotifications.notifyTeamBosses}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      taskNotifications: {
                        ...settings.taskNotifications,
                        notifyTeamBosses: event.target.checked,
                      },
                    })
                  }
                />
                Notificar chefes da equipe
              </label>
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={settings.taskNotifications.notifyDirectBoss}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      taskNotifications: {
                        ...settings.taskNotifications,
                        notifyDirectBoss: event.target.checked,
                      },
                    })
                  }
                />
                Notificar chefe direto do membro
              </label>
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={settings.taskNotifications.allowUserPreferences}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      taskNotifications: {
                        ...settings.taskNotifications,
                        allowUserPreferences: event.target.checked,
                      },
                    })
                  }
                />
                Permitir que cada usuário personalize suas notificações
              </label>
            </div>
          </PageCard>
        ) : null}

        {activeTab === 'notes' ? (
          <PageCard
            role="tabpanel"
            aria-label="Anotações"
            title="Anexos de anotações"
            subtitle="Limites de arquivos (PDF e imagens) enviados nas anotações de pessoas. Armazenamento no MinIO."
          >
            <div className="erp-form-grid">
              <label className="erp-label">
                Máximo de anexos por nota
                <input
                  className="erp-input"
                  type="number"
                  min={1}
                  max={20}
                  value={settings.notes.maxAttachments}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      notes: {
                        ...settings.notes,
                        maxAttachments: Number(event.target.value) || 1,
                      },
                    })
                  }
                />
              </label>
              <label className="erp-label">
                Tamanho máximo por arquivo (MB)
                <input
                  className="erp-input"
                  type="number"
                  min={1}
                  max={50}
                  step={1}
                  value={Math.round(settings.notes.maxAttachmentBytes / (1024 * 1024))}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      notes: {
                        ...settings.notes,
                        maxAttachmentBytes: Math.max(1, Number(event.target.value) || 1) * 1024 * 1024,
                      },
                    })
                  }
                />
              </label>
            </div>
            <p className="erp-hint mt-3">
              Extensões permitidas: PDF, PNG, JPG, JPEG, GIF e WEBP.
            </p>
          </PageCard>
        ) : null}

        {activeTab === 'taskQueue' ? (
          <PageCard
            role="tabpanel"
            aria-label="Fila de Tarefas"
            title="Fila de Tarefas (Hangfire)"
            subtitle="URL do worker em produção e credenciais de acesso ao dashboard. Quando preenchida, o painel aparece em Monitoramento."
          >
            <div className="erp-form-grid">
              <label className="erp-label erp-form-grid-full">
                URL base do worker
                <input
                  className="erp-input"
                  placeholder="https://worker.suaempresa.com"
                  value={settings.taskQueue.dashboardBaseUrl}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      taskQueue: { ...settings.taskQueue, dashboardBaseUrl: event.target.value },
                    })
                  }
                />
              </label>
              <label className="erp-label">
                Usuário (produção)
                <input
                  className="erp-input"
                  value={settings.taskQueue.username}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      taskQueue: { ...settings.taskQueue, username: event.target.value },
                    })
                  }
                  placeholder="Opcional — HTTP Basic Auth"
                />
              </label>
              <label className="erp-label">
                Senha (produção)
                <PasswordInput
                  className="erp-input pr-11 !mt-0"
                  wrapperClassName="mt-1 w-full"
                  value={settings.taskQueue.password}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      taskQueue: { ...settings.taskQueue, password: event.target.value },
                    })
                  }
                  placeholder="Opcional"
                />
              </label>
            </div>
            <p className="erp-hint mt-3">
              O acesso ao dashboard usa o token JWT do administrador logado. Em produção, deixe a URL
              vazia para usar o endereço público da aplicação (APP_PUBLIC_URL). Não abra /hangfire
              direto no navegador — use Monitor → Abrir em nova aba.
            </p>
          </PageCard>
        ) : null}

        <div className="erp-form-footer">
          <p className="text-sm text-secondary-500">As alterações de todas as abas são salvas juntas.</p>
          <div className="erp-btn-actions">
            <button type="submit" disabled={saving} className="erp-btn-primary">
              {saving ? 'Salvando...' : 'Salvar configurações'}
            </button>
          </div>
        </div>
      </form>
    </PageShell>
  );
}
